import { StrictMode, useEffect } from 'react'
import { createRoot } from 'react-dom/client'
import { CssBaseline, GlobalStyles, ThemeProvider } from '@mui/material'
import { AppRouter } from './app/router.js'
import { getTheme } from './ui/theme/theme.js'
import { resolveThemeMode, useUiStore } from './stores/uiStore.js'

function App(): JSX.Element {
  const themePreference = useUiStore((s) => s.themePreference)
  const mode = resolveThemeMode(themePreference)

  // Maintient l'attribut lu par le script anti-scintillement de index.html.
  useEffect(() => {
    document.documentElement.dataset['theme'] = mode
  }, [mode])

  return (
    <ThemeProvider theme={getTheme(mode)}>
      <CssBaseline />
      {/* ⚠️ flatpickr n'est pas un composant MUI : sa feuille de style est
          claire en dur. Sans cette adaptation, le calendrier s'ouvre en blanc
          par-dessus un formulaire sombre. Les couleurs viennent du THÈME, pas
          de valeurs écrites à la main, pour qu'un changement de palette les
          emporte avec lui. */}
      {mode === 'dark' && (
        <GlobalStyles
          styles={(theme) => ({
            '.flatpickr-calendar': {
              background: theme.palette.background.paper,
              color: theme.palette.text.primary,
              boxShadow: theme.shadows[8],
            },
            '.flatpickr-calendar.arrowTop::before, .flatpickr-calendar.arrowTop::after': {
              borderBottomColor: theme.palette.background.paper,
            },
            '.flatpickr-calendar.arrowBottom::before, .flatpickr-calendar.arrowBottom::after': {
              borderTopColor: theme.palette.background.paper,
            },
            '.flatpickr-months .flatpickr-month, .flatpickr-current-month input.cur-year, .flatpickr-monthDropdown-months':
              {
                background: theme.palette.background.paper,
                color: theme.palette.text.primary,
                fill: theme.palette.text.primary,
              },
            '.flatpickr-weekday': { background: 'transparent', color: theme.palette.text.secondary },
            '.flatpickr-day': { color: theme.palette.text.primary },
            '.flatpickr-day.flatpickr-disabled, .flatpickr-day.prevMonthDay, .flatpickr-day.nextMonthDay':
              { color: theme.palette.text.disabled },
            '.flatpickr-day:hover': { background: theme.palette.action.hover, border: 'none' },
            '.flatpickr-day.selected, .flatpickr-day.selected:hover': {
              background: theme.palette.primary.main,
              borderColor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
            },
            '.flatpickr-months .flatpickr-prev-month svg, .flatpickr-months .flatpickr-next-month svg':
              { fill: theme.palette.text.primary },
          })}
        />
      )}
      <AppRouter />
    </ThemeProvider>
  )
}

const container = document.getElementById('root')
if (!container) throw new Error("Élément #root introuvable dans index.html")

createRoot(container).render(
  <StrictMode>
    <App />
  </StrictMode>,
)

/**
 * Service Worker — seulement en production.
 *
 * En développement, il servirait une coquille en cache pendant que Vite pousse
 * des modules à chaud : on passerait un temps fou à débusquer des changements
 * qui « ne s'appliquent pas ».
 */
if ('serviceWorker' in navigator && import.meta.env.PROD) {
  window.addEventListener('load', () => {
    void navigator.serviceWorker.register(`/sw.js?v=${__BUILD_ID__}`).catch(() => {
      // Un enregistrement refusé (mode privé, HTTP simple) ne doit pas
      // empêcher l'application de fonctionner : elle marche sans.
    })
  })
}
